// Scene-agnostic splat-mesh updater — THE present path for OGS splats. The
// per-update PLY-rebuild + coalescing logic drives a SplatMesh inside an
// EXTERNAL three.js scene, i.e. the host React-Three-Fiber `<Canvas>` scene
// (OGSModel.jsx).
//
// The SparkRenderer is passed in by the caller (find-or-create, so we never
// spawn a second one — two would double-render, see HairSplat.jsx).

import * as THREE from "three";
import { SplatMesh, SplatFileType, type PackedSplats } from "@sparkjsdev/spark";
import type { Gaussians } from "../inference/lift";

/** Place a freshly built SplatMesh in the scene: uniform cm→m scale, the bundle's
 *  world_rotation, then the scene-space offset — three.js composes them as
 *  T·R·S, i.e. `world = rotation·(local·scale) + offset`, which is the placement
 *  the plücker camera inverts (inference/plucker.ts toLocalOrigin/toLocalRotation).
 *  Shared by BOTH build paths — the packed branch used to apply only the scale,
 *  silently dropping world_offset (a head bundle would render at the training
 *  frame's origin instead of standing height). */
export function applyMeshPlacement(
  mesh: {
    scale: { setScalar: (v: number) => void };
    position: { set: (x: number, y: number, z: number) => void };
    quaternion: { copy: (q: THREE.Quaternion) => unknown };
  },
  meshScale: number,
  meshOffset: readonly [number, number, number],
  meshRotation: THREE.Quaternion | null = null,
): void {
  if (meshScale !== 1) mesh.scale.setScalar(meshScale);
  if (meshRotation) mesh.quaternion.copy(meshRotation);
  const [ox, oy, oz] = meshOffset;
  if (ox !== 0 || oy !== 0 || oz !== 0) mesh.position.set(ox, oy, oz);
}

/** Mark an IN-PLACE packed update dirty. BOTH flags are required — despite the
 *  identical name they are different mechanisms:
 *    PackedSplats.needsUpdate — a plain field; re-uploads the packed words to
 *      the GPU texture.
 *    SplatMesh.needsUpdate    — a SplatGenerator SETTER (→ updateVersion());
 *      bumps the generator version that makes Spark REGENERATE the accumulated
 *      splats.
 *  With only the first, Spark re-uploads the buffer but never regenerates, so it
 *  keeps drawing the last accumulation until its own `viewChanged` test fires —
 *  i.e. the avatar animates ONLY while the camera is moving and freezes the
 *  instant it stops. Same failure mode (and fix) as cameraSeeThrough's mesh
 *  version bump. The PLY path never hit this: it built a new SplatMesh per
 *  update, which regenerates unconditionally. */
export function markPackedDirty(
  packedSplats: { needsUpdate: boolean },
  mesh: { needsUpdate: boolean } | undefined,
): void {
  packedSplats.needsUpdate = true;
  if (mesh) mesh.needsUpdate = true;
}

export class SplatMeshUpdater {
  private mesh?: SplatMesh;
  /** Optional cap on splat count. */
  splatCap: number | null = null;
  /** Uniform scale applied to the SplatMesh (cm→m worldScale). The lift emits
   *  positions in the training cm frame; scaling the mesh maps it into the
   *  scene's meter frame. 1 = render verbatim. */
  meshScale = 1;
  /** Scene-space placement (metres), applied AFTER meshScale — moves the splat
   *  out of the training frame's origin (e.g. lift a head bundle to standing
   *  height so the floor grid doesn't slice it). [0,0,0] = render in place. */
  meshOffset: [number, number, number] = [0, 0, 0];
  /** scene.json world_rotation as a quaternion — the training frame's own
   *  orientation, undone here so the avatar renders upright. The Gaussians and
   *  everything feeding the decoders stay in the trained frame; only this mesh
   *  turns. null = the bundle declares none (render/worldRotation.ts). */
  meshRotation: THREE.Quaternion | null = null;
  /** Optional timing hook: called with the ms spent packing the PLY +
   *  building the replacement SplatMesh — the async half of an update that
   *  updateGaussians only queues. Used by OGSModel's perf accumulator. */
  onMeshBuilt: ((ms: number) => void) | null = null;

  // Update coalescing — runAppearance can fire faster than sparkjs can absorb an
  // update, so we keep only the latest pending data and process it serially.
  private pending: Gaussians | null = null;
  // In-place packed path (GpuLifter.packedMode): a snapshot of the GPU packed
  // words. ONE SplatMesh is built once via constructSplats (which allocates the
  // sparkjs GPU source), then its packedArray is mutated each frame — no PLY pack
  // / no `new SplatMesh`. `editable: true` + constructSplats is the canonical
  // procedural-splat path; the raw {packedArray} ctor doesn't init the GPU source.
  private pendingPacked: { count: number; packed: Uint32Array; capacity: number } | null = null;
  private packedSplats?: PackedSplats;
  private inFlight = false;
  private disposed = false;
  private visible = true;

  /**
   * `parent` is the object splat meshes attach to. Widened from Scene to
   * Object3D (ENG-895) so a splat avatar can hang under its own root Group and
   * be transformed by the gizmo: this class keeps owning each mesh's LOCAL
   * meshScale/meshOffset, which it re-applies on every rebuild, while the group
   * above it owns the user's placement. Attaching straight to the scene (as it
   * did) meant those per-rebuild writes were the only owner, so any gizmo edit
   * was overwritten on the next rig update.
   */
  private parent: THREE.Object3D;

  // Explicit assignment, NOT a TS parameter property: node's strip-only TS mode
  // rejects those, and tests/unit/splatMeshUpdaterOffset loads this module.
  constructor(parent: THREE.Object3D) {
    this.parent = parent;
  }

  /** Show/hide the splat without tearing it down (blueprint renderer-node gate). */
  setVisible(v: boolean) {
    this.visible = v;
    if (this.mesh) this.mesh.visible = v;
  }

  updateGaussians(g: Gaussians) {
    if (this.disposed) return;
    if (g.packed) {
      // The packed lift writes each splat to a FIXED texel slot, scattering valid
      // splats across [0, capacity) with culled slots cleared to transparent — so
      // the WHOLE buffer must be uploaded with numSplats == capacity. A prefix cap
      // (splatCap, meaningful only for the PLY path) would drop scattered splats
      // and render culled-transparent ones, so it does not apply in packed mode.
      const capacity = g.packed.length / 4;
      if (this.splatCap != null && this.splatCap < capacity) {
        console.warn(`[isaac] splatCap=${this.splatCap} ignored in packed mode: the fixed-slot layout needs the full ${capacity}-slot buffer.`);
      }
      // Snapshot the packed words (g.packed is the lifter's persistent buffer,
      // overwritten next lift).
      this.pendingPacked = { count: capacity, packed: new Uint32Array(g.packed.subarray(0, capacity * 4)), capacity };
      this.pending = null;
    } else {
      // Legacy PLY-rebuild path (CPU GaussianLifter, no packed buffer). splatCap
      // caps the prefix here (contiguous SoA), unlike the scattered packed layout.
      const N = Math.min(g.count, this.splatCap ?? g.count);
      this.pending = {
        count: N,
        positions: new Float32Array(g.positions.subarray(0, N * 3)),
        scales: new Float32Array(g.scales.subarray(0, N * 3)),
        rotations: new Float32Array(g.rotations.subarray(0, N * 4)),
        colors: new Float32Array(g.colors.subarray(0, N * 3)),
        opacities: new Float32Array(g.opacities.subarray(0, N)),
      };
      this.pendingPacked = null;
    }
    if (this.inFlight) return;
    this.runUpdateLoop().catch((e) => console.warn("[isaac] splat update loop error", e));
  }

  private async runUpdateLoop() {
    this.inFlight = true;
    try {
      while (this.pending || this.pendingPacked) {
        const t0 = performance.now();
        if (this.pendingPacked) {
          const next = this.pendingPacked;
          this.pendingPacked = null;
          if (!this.packedSplats) {
            // Build ONCE via constructSplats (allocates + uploads the GPU source).
            const ps = next.packed, cnt = next.count, cap = next.capacity;
            const mesh = new SplatMesh({
              maxSplats: cap,
              editable: true,
              constructSplats: (splats) => {
                const pa = splats.ensureSplats(cap);
                pa.set(ps.subarray(0, cnt * 4), 0);
                splats.numSplats = cnt;
              },
            });
            applyMeshPlacement(mesh, this.meshScale, this.meshOffset, this.meshRotation);
            try { await mesh.initialized; } catch { /* fall through */ }
            if (this.disposed) { mesh.dispose?.(); return; }
            this.packedSplats = mesh.packedSplats;
            this.mesh = mesh;
            mesh.visible = this.visible;
            this.parent.add(mesh);
          } else if (next.count * 4 <= this.packedSplats.packedArray!.length) {
            // In-place: overwrite every slot (packed mode uploads the full
            // capacity — see updateGaussians); numSplats == capacity.
            this.packedSplats.packedArray!.set(next.packed.subarray(0, next.count * 4), 0);
            this.packedSplats.numSplats = next.count;
            markPackedDirty(this.packedSplats, this.mesh);
          } else {
            // Splat count outgrew the mesh built at first update — a rebuild would
            // be needed. Warn rather than silently drop the update.
            console.warn(`[isaac] packed update dropped: count ${next.count} exceeds mesh capacity ${this.packedSplats.packedArray!.length / 4}.`);
          }
          this.onMeshBuilt?.(performance.now() - t0);
          continue;
        }
        const next = this.pending!;
        this.pending = null;
        const fileBytes = packPlyBuffer(next, next.count);
        const newMesh = new SplatMesh({ fileBytes, fileType: SplatFileType.PLY });
        applyMeshPlacement(newMesh, this.meshScale, this.meshOffset, this.meshRotation);
        try { await newMesh.initialized; } catch { /* fall through */ }
        if (this.disposed) { newMesh.dispose?.(); return; }
        if (this.mesh) {
          this.parent.remove(this.mesh);
          this.mesh.dispose?.();
        }
        this.mesh = newMesh;
        newMesh.visible = this.visible;
        this.parent.add(newMesh);
        this.onMeshBuilt?.(performance.now() - t0);
      }
    } finally {
      this.inFlight = false;
    }
  }

  /** Remove the splat mesh from the scene and release it. Safe to call twice.
   *  Does NOT touch the SparkRenderer — that's owned by the caller. */
  dispose() {
    this.disposed = true;
    this.pending = null;
    this.pendingPacked = null;
    if (this.mesh) {
      this.parent.remove(this.mesh);
      this.mesh.dispose?.();
      this.mesh = undefined;
    }
    this.packedSplats = undefined;
  }
}

/**
 * Pack Gaussians into a standard 3DGS PLY (binary little-endian).
 * Each splat is 17 float32 values: position (3) + normal (3) + f_dc (3)
 * + opacity (logit) + scale (log) + rotation (4, w/x/y/z).
 *
 * The lifter stores opacity as logit and rotation as (w,x,y,z); colors
 * are raw RGB in [0,1] and scales are raw (post-sqrt-of-eigenvalue).
 * The PLY reader applies sigmoid + exp + the SH_C0*x+0.5 conversion at
 * load, so we invert each here.
 */
export function packPlyBuffer(g: Gaussians, cap: number): Uint8Array {
  const N = Math.min(g.count, cap);
  const SH_C0 = 0.28209479177387814;

  const header =
    "ply\n" +
    "format binary_little_endian 1.0\n" +
    `element vertex ${N}\n` +
    "property float x\nproperty float y\nproperty float z\n" +
    "property float nx\nproperty float ny\nproperty float nz\n" +
    "property float f_dc_0\nproperty float f_dc_1\nproperty float f_dc_2\n" +
    "property float opacity\n" +
    "property float scale_0\nproperty float scale_1\nproperty float scale_2\n" +
    "property float rot_0\nproperty float rot_1\nproperty float rot_2\nproperty float rot_3\n" +
    "end_header\n";
  const headerBytes = new TextEncoder().encode(header);
  const FLOATS_PER_SPLAT = 17;
  const out = new Uint8Array(headerBytes.length + N * FLOATS_PER_SPLAT * 4);
  out.set(headerBytes, 0);
  const dv = new DataView(
    out.buffer,
    out.byteOffset + headerBytes.length,
    N * FLOATS_PER_SPLAT * 4,
  );

  for (let i = 0; i < N; i++) {
    let off = i * FLOATS_PER_SPLAT * 4;
    dv.setFloat32(off, g.positions[i * 3 + 0], true); off += 4;
    dv.setFloat32(off, g.positions[i * 3 + 1], true); off += 4;
    dv.setFloat32(off, g.positions[i * 3 + 2], true); off += 4;
    dv.setFloat32(off, 0, true); off += 4;
    dv.setFloat32(off, 0, true); off += 4;
    dv.setFloat32(off, 0, true); off += 4;
    dv.setFloat32(off, (g.colors[i * 3 + 0] - 0.5) / SH_C0, true); off += 4;
    dv.setFloat32(off, (g.colors[i * 3 + 1] - 0.5) / SH_C0, true); off += 4;
    dv.setFloat32(off, (g.colors[i * 3 + 2] - 0.5) / SH_C0, true); off += 4;
    // Opacity is already stored as logit in the lifter — straight through.
    dv.setFloat32(off, g.opacities[i], true); off += 4;
    dv.setFloat32(off, Math.log(Math.max(g.scales[i * 3 + 0], 1e-12)), true); off += 4;
    dv.setFloat32(off, Math.log(Math.max(g.scales[i * 3 + 1], 1e-12)), true); off += 4;
    dv.setFloat32(off, Math.log(Math.max(g.scales[i * 3 + 2], 1e-12)), true); off += 4;
    dv.setFloat32(off, g.rotations[i * 4 + 0], true); off += 4;
    dv.setFloat32(off, g.rotations[i * 4 + 1], true); off += 4;
    dv.setFloat32(off, g.rotations[i * 4 + 2], true); off += 4;
    dv.setFloat32(off, g.rotations[i * 4 + 3], true); off += 4;
  }
  return out;
}

